/**
 * The static stylesheet every mount adopts into its shadow root: the reset
 * the host page cannot reach, the font stack, the few keyframes. Everything
 * that DEPENDS on the config - colours, radius, offsets - is an inline
 * style on the element, ported from the dashboard preview; the customer's
 * `customCss` and each block's `css` are appended after this, per mount
 * (`mount.ts`), so they win on specificity ties.
 *
 * `--font-display` is read as a custom property because custom properties
 * inherit across the shadow boundary: a page that defines it gets its own
 * display face on a popup's headline, the way the dashboard's mock did.
 */
export const BASE_CSS = `
:host{all:initial;display:contents;font-family:system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;font-size:14px;line-height:1.4;-webkit-font-smoothing:antialiased;color:#15171e}
:host([hidden]){display:none}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
button{font:inherit;color:inherit;background:none;border:0;cursor:pointer;-webkit-tap-highlight-color:transparent}
input{font:inherit;color:inherit;background:none;border:0;outline:0;min-width:0}
input::placeholder{color:var(--uq-placeholder,inherit);opacity:1}
input:disabled{cursor:not-allowed}
button:disabled{cursor:default}
a{color:inherit;text-decoration:none}
img,video{display:block;max-width:100%}
.uq-fixed{position:fixed;z-index:2147483000}
.uq-inline{display:block}
.uq-dots{display:inline-flex;gap:3px;align-items:center;height:10px}
.uq-dots span{width:5px;height:5px;border-radius:50%;background:currentColor;opacity:.35;animation:uq-dot 1.2s infinite ease-in-out}
.uq-dots span:nth-child(2){animation-delay:.15s}
.uq-dots span:nth-child(3){animation-delay:.3s}
@keyframes uq-dot{0%,80%,100%{opacity:.25;transform:translateY(0)}40%{opacity:.9;transform:translateY(-2px)}}
.uq-rise{animation:uq-rise .22s ease-out both}
@keyframes uq-rise{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.uq-fade{animation:uq-fade .18s ease-out both}
@keyframes uq-fade{from{opacity:0}to{opacity:1}}
.uq-drawer{left:0;right:0;bottom:0;display:flex;flex-direction:column;overflow:hidden;padding-bottom:env(safe-area-inset-bottom);transition:height .28s cubic-bezier(.32,.72,0,1),border-radius .28s ease}
.uq-drawer-half{height:50%}
.uq-drawer-full{height:100%}
.uq-drawer-held{transition:none}
.uq-grab{flex-shrink:0;height:22px;display:flex;align-items:center;justify-content:center;cursor:grab;touch-action:none;user-select:none;-webkit-user-select:none}
.uq-grab:active{cursor:grabbing}
.uq-grab span{width:36px;height:4px;border-radius:2px}
.uq-slide{animation:uq-slide .3s cubic-bezier(.32,.72,0,1) both}
@keyframes uq-slide{from{transform:translateY(100%)}to{transform:none}}
@media (prefers-reduced-motion:reduce){.uq-rise,.uq-fade,.uq-slide,.uq-dots span{animation:none}.uq-drawer{transition:none}}
`
